import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Button,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Home as HomeIcon,
  AccountBalance as AccountBalanceIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
  Warning as WarningIcon,
  TrendingUp as HighIcon,
  TrendingDown as LowIcon,
  Remove as NormalIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpOutlineIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Queue as QueueIcon,
  History as HistoryIcon,
  Send as SendIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../../utils/config';
import { PaymentMatchingSection, BillsReadyToPaySection } from './components';
import { NoteIcon } from '../../../../components/accounting/NoteIcon';

interface QueueEntry {
  id: number;
  queueReference: string;
  sourceCompany: string;
  paymentType: string;
  sourceAmount: number;
  sourceCustomerName: string;
  sourceTxnDate: string;
  matchConfidence: string;
  confidenceScore: number;
  status: string;
  priority: string;
  createdAt: string;
  approvedBy?: string;
  approvedAt?: string;
  executedAt?: string;
  matchedBillIds?: string[];
  matchedBillAmount?: number;
  vendorName?: string;
  hasNotes?: boolean;
}

interface QueueSummary {
  pending: number;
  submitted: number;
  executed: number;
  totalPendingAmount: number;
  manualRequiredCount: number;
}

interface Filters {
  status: string;
  sourceCompany: string;
  matchConfidence: string;
  priority: string;
  search: string;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'executed', label: 'Paid' },
];

const COMPANY_OPTIONS = [
  { value: '', label: 'All Companies' },
  { value: 'horwath', label: 'Horwath HTL' },
  { value: 'barrett', label: 'Barrett' },
];

const CONFIDENCE_OPTIONS = [
  { value: '', label: 'All Confidence Levels' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'manual_required', label: 'Manual Required' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

const getStatusChip = (status: string) => {
  const statusConfig: Record<string, { color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; icon: React.ReactElement; label: string }> = {
    pending: { color: 'warning', icon: <PendingIcon fontSize="small" />, label: 'Pending' },
    submitted: { color: 'info', icon: <SendIcon fontSize="small" />, label: 'Submitted' },
    executed: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Paid' },
  };

  const config = statusConfig[status] || { color: 'default' as const, icon: <PendingIcon fontSize="small" />, label: status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) };

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size="small"
    />
  );
};

const getConfidenceChip = (confidence: string, score: number) => {
  const colorMap: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
    high: 'success',
    medium: 'warning',
    low: 'error',
    manual_required: 'default',
  };

  return (
    <Tooltip title={`Confidence Score: ${score.toFixed(1)}%`}>
      <Chip
        label={`${confidence.replace(/_/g, ' ')} (${score.toFixed(0)}%)`}
        color={colorMap[confidence] || 'default'}
        size="small"
        variant="outlined"
      />
    </Tooltip>
  );
};

const getPriorityIcon = (priority: string) => {
  switch (priority) {
    case 'urgent':
      return <HighIcon color="error" />;
    case 'high':
      return <HighIcon color="warning" />;
    case 'normal':
      return <NormalIcon color="action" />;
    case 'low':
      return <LowIcon color="disabled" />;
    default:
      return <NormalIcon color="action" />;
  }
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reconciliation-tabpanel-${index}`}
      aria-labelledby={`reconciliation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

// Tab name to index mapping
const TAB_NAMES = ['matching', 'bills', 'queue', 'history'] as const;
type TabName = typeof TAB_NAMES[number];

const getTabIndex = (tabName: string | null): number => {
  if (!tabName) return 0;
  const index = TAB_NAMES.indexOf(tabName as TabName);
  return index >= 0 ? index : 0;
};

export const ReconciliationQueuePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [activeTab, setActiveTab] = useState(() => getTabIndex(searchParams.get('tab')));
  const [entries, setEntries] = useState<QueueEntry[]>([]);
  const [summary, setSummary] = useState<QueueSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [unmatchedCount, setUnmatchedCount] = useState(0);
  const [billsReadyCount, setBillsReadyCount] = useState(0);
  const [historyEntries, setHistoryEntries] = useState<QueueEntry[]>([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(25);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    status: '',
    sourceCompany: '',
    matchConfidence: '',
    priority: '',
    search: '',
  });

  // Status update state
  const [statusUpdateLoading, setStatusUpdateLoading] = useState<number | null>(null);
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertEntryId, setRevertEntryId] = useState<number | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('pageSize', String(rowsPerPage));

      // For Approved Queue, show pending and submitted entries unless explicitly filtered
      if (filters.status) {
        params.append('status', filters.status);
      } else {
        // Default: show pending and submitted (not executed/paid)
        params.append('status', 'pending,submitted');
      }
      if (filters.sourceCompany) params.append('sourceCompany', filters.sourceCompany);
      if (filters.matchConfidence) params.append('matchConfidence', filters.matchConfidence);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.search) params.append('search', filters.search);

      const response = await authenticatedFetch(`${API_URL}/api/accounting/reconciliation/queue?${params}`);
      if (!response) {
        throw new Error('No response received');
      }
      const data = await response.json();

      if (data.success) {
        setEntries(data.data.items);
        setTotalCount(data.data.pagination.total);
        setSummary(data.data.summary);
      } else {
        setError(data.error || 'Failed to load queue');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, page, rowsPerPage, filters]);

  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);

      const params = new URLSearchParams();
      params.append('page', String(historyPage + 1));
      params.append('pageSize', String(historyRowsPerPage));
      params.append('status', 'executed');

      const response = await authenticatedFetch(`${API_URL}/api/accounting/reconciliation/queue?${params}`);
      if (!response) {
        throw new Error('No response received');
      }
      const data = await response.json();

      if (data.success) {
        setHistoryEntries(data.data.items);
        setHistoryTotalCount(data.data.pagination.total);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [authenticatedFetch, historyPage, historyRowsPerPage]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Fetch counts for tab badges
  const fetchCounts = useCallback(async () => {
    try {
      // Fetch unmatched payments count
      const unmatchedResponse = await authenticatedFetch(
        `${API_URL}/api/accounting/payments/unmatched?matchStatus=unmatched,candidate_suggested,awaiting_response&pageSize=1`
      );
      if (unmatchedResponse) {
        const unmatchedData = await unmatchedResponse.json();
        if (unmatchedData.success) {
          setUnmatchedCount(unmatchedData.data.pagination.total);
        }
      }

      // Fetch bills ready to pay count
      const billsResponse = await authenticatedFetch(`${API_URL}/api/accounting/bills/ready-to-pay`);
      if (billsResponse) {
        const billsData = await billsResponse.json();
        if (billsData.success) {
          setBillsReadyCount(billsData.data.summary.count || 0);
        }
      }

    } catch (err) {
      // Silently fail - these are just for badges
      console.error('Failed to fetch counts:', err);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Update URL with tab name
    setSearchParams({ tab: TAB_NAMES[newValue] }, { replace: true });
    // Refresh data when switching to Approved Queue tab (now index 2)
    if (newValue === 2) {
      fetchQueue();
    }
    // Refresh data when switching to History tab (now index 3)
    if (newValue === 3) {
      fetchHistory();
    }
    // Refresh counts on any tab change
    fetchCounts();
  };

  const handleHistoryChangePage = (_: unknown, newPage: number) => {
    setHistoryPage(newPage);
  };

  const handleHistoryChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHistoryRowsPerPage(parseInt(event.target.value, 10));
    setHistoryPage(0);
  };

  // Fetch history when pagination changes (tab index 3)
  useEffect(() => {
    if (activeTab === 3) {
      fetchHistory();
    }
  }, [activeTab, historyPage, historyRowsPerPage, fetchHistory]);

  const handleRefreshAll = () => {
    fetchQueue();
    fetchCounts();
  };

  // Update queue item status
  const handleUpdateStatus = async (id: number, newStatus: 'pending' | 'submitted') => {
    try {
      setStatusUpdateLoading(id);
      const response = await authenticatedFetch(`${API_URL}/api/accounting/reconciliation/queue/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response) {
        throw new Error('No response received');
      }

      const data = await response.json();

      if (data.success) {
        setSnackbarMessage(newStatus === 'submitted' ? 'Marked as submitted' : 'Reverted to pending');
        setSnackbarSeverity('success');
        fetchQueue(); // Refresh the list
      } else {
        throw new Error(data.error || 'Failed to update status');
      }
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to update status');
      setSnackbarSeverity('error');
    } finally {
      setStatusUpdateLoading(null);
      setRevertDialogOpen(false);
      setRevertEntryId(null);
    }
  };

  // Mark as submitted (no confirmation needed)
  const handleMarkSubmitted = (id: number) => {
    handleUpdateStatus(id, 'submitted');
  };

  // Open revert confirmation dialog
  const handleOpenRevertDialog = (id: number) => {
    setRevertEntryId(id);
    setRevertDialogOpen(true);
  };

  // Confirm revert to pending
  const handleConfirmRevert = () => {
    if (revertEntryId) {
      handleUpdateStatus(revertEntryId, 'pending');
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof Filters) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
    setPage(0);
  };

  const handleRowClick = (id: number) => {
    navigate(`/admin/hub/accounting/reconciliation/${id}`);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        <Typography color="text.primary">Payment Reconciliation</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AccountBalanceIcon sx={{ mr: 1, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1">
              Payment Reconciliation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Match incoming payments to invoices and manage bill payments between Horwath HTL and Barrett.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefreshAll}
          disabled={loading}
        >
          Refresh All
        </Button>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            icon={
              <Badge badgeContent={unmatchedCount} color="warning" max={99}>
                <ReceiptIcon />
              </Badge>
            }
            iconPosition="start"
            label="Payment Matching"
          />
          <Tab
            icon={
              <Badge badgeContent={billsReadyCount} color="success" max={99}>
                <PaymentIcon />
              </Badge>
            }
            iconPosition="start"
            label="Bills Ready to Pay"
          />
          <Tab
            icon={
              <Badge badgeContent={(summary?.pending || 0) + (summary?.submitted || 0)} color="info" max={99}>
                <QueueIcon />
              </Badge>
            }
            iconPosition="start"
            label="Approved Queue"
          />
          <Tab
            icon={
              <Badge badgeContent={summary?.executed || 0} color="default" max={99}>
                <HistoryIcon />
              </Badge>
            }
            iconPosition="start"
            label="History"
          />
        </Tabs>
      </Paper>

      {/* Tab 0: Payment Matching Section */}
      <TabPanel value={activeTab} index={0}>
        <PaymentMatchingSection onRefresh={handleRefreshAll} />
      </TabPanel>

      {/* Tab 1: Bills Ready to Pay */}
      <TabPanel value={activeTab} index={1}>
        <BillsReadyToPaySection onRefresh={handleRefreshAll} />
      </TabPanel>

      {/* Tab 2: Approved Queue */}
      <TabPanel value={activeTab} index={2}>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="warning.main">{summary.pending}</Typography>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="info.main">{summary.submitted}</Typography>
                <Typography variant="body2" color="text.secondary">Submitted</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="success.main">{summary.executed}</Typography>
                <Typography variant="body2" color="text.secondary">Paid</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h5" color="primary.main">
                  {formatCurrency(summary.totalPendingAmount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Pending Amount</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="default">{summary.manualRequiredCount}</Typography>
                <Typography variant="body2" color="text.secondary">Manual Review</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* How This Page Works - Expandable Help Panel */}
      <Accordion sx={{ mb: 2 }} defaultExpanded={false}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="help-panel-content"
          id="help-panel-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpOutlineIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="medium">
              What is this page?
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ maxWidth: 900 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Cross-Company Payment Reconciliation
            </Typography>

            <Typography variant="body2" paragraph>
              This queue shows <strong>payments received in one company (Horwath or Barrett) that need to trigger
              a corresponding bill payment in the other company</strong>.
            </Typography>

            <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
              The Scenario
            </Typography>
            <Typography variant="body2" paragraph>
              When Horwath does work for a client but Barrett is involved (or vice versa), there's inter-company billing:
            </Typography>
            <List dense sx={{ pl: 2, mb: 2 }}>
              <ListItem disableGutters sx={{ py: 0.25 }}>
                <ListItemText primary="1. Horwath invoices Barrett for services" primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
              <ListItem disableGutters sx={{ py: 0.25 }}>
                <ListItemText primary="2. Barrett has a corresponding bill payable to Horwath" primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            </List>

            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              What the System Does
            </Typography>
            <List dense sx={{ pl: 2, mb: 2 }}>
              <ListItem disableGutters sx={{ py: 0.25 }}>
                <ListItemText
                  primary="1. Detection"
                  secondary="When a customer payment comes into Horwath's QuickBooks (via webhook or polling), the system detects it"
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                  secondaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem disableGutters sx={{ py: 0.25 }}>
                <ListItemText
                  primary="2. Matching"
                  secondary="It looks for corresponding unpaid bills in Barrett's QuickBooks that should be paid as a result"
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                  secondaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem disableGutters sx={{ py: 0.25 }}>
                <ListItemText
                  primary="3. Queue"
                  secondary="The matched payment + bills go into this queue for human review and approval"
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                  secondaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem disableGutters sx={{ py: 0.25 }}>
                <ListItemText
                  primary="4. Execution (after approval)"
                  secondary="Creates a BillPayment in Barrett to pay the Horwath bills, and a mirror 'Receive Payment' in Horwath to close out the invoice"
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                  secondaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>In summary:</strong> Each queue item represents a cross-company payment event —
                "Company A received money, and Company B needs to pay their corresponding bill to Company A."
              </Typography>
            </Alert>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by reference, customer..."
              value={filters.search}
              onChange={handleFilterChange('search')}
              aria-label="Search reconciliation queue"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={handleFilterChange('status')}
              >
                {STATUS_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Company</InputLabel>
              <Select
                value={filters.sourceCompany}
                label="Company"
                onChange={handleFilterChange('sourceCompany')}
              >
                {COMPANY_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Confidence</InputLabel>
              <Select
                value={filters.matchConfidence}
                label="Confidence"
                onChange={handleFilterChange('matchConfidence')}
              >
                {CONFIDENCE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                label="Priority"
                onChange={handleFilterChange('priority')}
              >
                {PRIORITY_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Queue Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Reference</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Bill #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell align="right">Bill Amount</TableCell>
              <TableCell>Payment Date</TableCell>
              <TableCell>Match Confidence</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell align="center" sx={{ width: 40 }}></TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No queue entries found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  hover
                  onClick={() => handleRowClick(entry.id)}
                  sx={{
                    cursor: 'pointer',
                    ...(entry.status === 'submitted' && {
                      backgroundColor: 'rgba(33, 150, 243, 0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.12)',
                      },
                    }),
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {entry.queueReference}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.vendorName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.matchedBillIds?.join(', ') || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.sourceCustomerName || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(entry.matchedBillAmount ?? entry.sourceAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(entry.sourceTxnDate)}</TableCell>
                  <TableCell>
                    {getConfidenceChip(entry.matchConfidence, entry.confidenceScore)}
                  </TableCell>
                  <TableCell>{getStatusChip(entry.status)}</TableCell>
                  <TableCell>
                    <Tooltip title={entry.priority}>
                      {getPriorityIcon(entry.priority)}
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <NoteIcon hasNotes={entry.hasNotes || false} />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      {/* Status action icons */}
                      {entry.status === 'pending' && (
                        <Tooltip title="Mark as Submitted">
                          <IconButton
                            size="small"
                            color="info"
                            disabled={statusUpdateLoading === entry.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkSubmitted(entry.id);
                            }}
                          >
                            {statusUpdateLoading === entry.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <SendIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {entry.status === 'submitted' && (
                        <Tooltip title="Revert to Pending">
                          <IconButton
                            size="small"
                            color="warning"
                            disabled={statusUpdateLoading === entry.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenRevertDialog(entry.id);
                            }}
                          >
                            {statusUpdateLoading === entry.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <UndoIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* View details icon */}
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(entry.id);
                          }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      </TabPanel>

      {/* Tab 3: History (Paid) */}
      <TabPanel value={activeTab} index={3}>
        <Typography variant="h6" gutterBottom>
          Payment History
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Bills that have been paid.
        </Typography>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Bill #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Bill Amount</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Paid At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : historyEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No executed entries found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                historyEntries.map((entry) => (
                  <TableRow
                    key={entry.id}
                    hover
                    onClick={() => handleRowClick(entry.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {entry.queueReference}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {entry.vendorName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {entry.matchedBillIds?.join(', ') || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {entry.sourceCustomerName || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(entry.matchedBillAmount ?? entry.sourceAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(entry.sourceTxnDate)}</TableCell>
                    <TableCell>
                      {entry.executedAt ? formatDate(entry.executedAt) : '-'}
                    </TableCell>
                    <TableCell>{getStatusChip(entry.status)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(entry.id);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={historyTotalCount}
            rowsPerPage={historyRowsPerPage}
            page={historyPage}
            onPageChange={handleHistoryChangePage}
            onRowsPerPageChange={handleHistoryChangeRowsPerPage}
          />
        </TableContainer>
      </TabPanel>

      {/* Revert to Pending Confirmation Dialog */}
      <Dialog
        open={revertDialogOpen}
        onClose={() => setRevertDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Revert to Pending?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to revert this entry back to Pending status?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmRevert}
            color="warning"
            variant="contained"
            disabled={statusUpdateLoading !== null}
          >
            {statusUpdateLoading !== null ? <CircularProgress size={20} /> : 'Revert to Pending'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Snackbar */}
      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarMessage(null)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReconciliationQueuePage;
